import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { encrypt } from "../../utils/encryption";
import Button from "../../components/common/Button";
import ErrorAlert from "../../components/common/ErrorAlert";
import CrudTable from "../../components/common/crud/CrudTable";
import useCrud from "../../components/common/crud/useCrud";
import alertService from "../../utils/alertService";
import { formatDate } from "../../utils/dateUtils";
import apiClient from "../../services/apiClient";
import blService from "../../services/blService";

function PurchaseMasterListPage() {
  const navigate = useNavigate();
  const basePath = "/purchase-master";

  const endpoints = useMemo(
    () => ({
      list: "/purchase-master",
      create: "/purchase-master",
      edit: (recordId) => `/purchase-master/${recordId}`,
      update: (recordId) => `/purchase-master/${recordId}`,
      delete: (recordId) => `/purchase-master/${recordId}`,
    }),
    [],
  );

  const {
    rows,
    isLoading,
    error,
    filters,
    pagination,
    sortConfig,
    askDelete,
    changePage,
    changeSort,
    updateFilters,
  } = useCrud({
    endpoints,
    idKey: "purchaseMasterId",
    updateMethod: "patch",
    initialFilters: { search: "" },
    initialSort: { key: "createdDate", order: "DESC" },
  });

  const tableColumns = useMemo(
    () => [
      {
        key: "billNo",
        label: "Bill No",
        sortable: true,
        className: "font-bold text-slate-800 dark:text-white",
        render: (item) => String(item?.billNo || "-"),
      },
      {
        key: "billDate",
        label: "Bill Date",
        sortable: true,
        render: (item) => formatDate(item?.billDate),
      },
      {
        key: "vendorName",
        label: "Vendor",
        sortable: true,
        render: (item) => item?.vendor?.vendorName || item?.vendorName || "-",
      },
      {
        key: "bookingNo",
        label: "Booking No",
        sortable: true,
        render: (item) => String(item?.bookingNo || "-"),
      },
      {
        key: "noOfContainers",
        label: "Containers",
        sortable: true,
        headerClassName: "text-right",
        className: "text-right font-bold text-primary-600",
      },
      {
        key: "netWeight",
        label: "Net Weight (KG)",
        sortable: true,
        headerClassName: "text-right",
        className: "text-right font-mono font-bold",
        render: (item) =>
          item?.netWeight ? Number(item.netWeight).toLocaleString() : "0",
      },
      {
        key: "billPDF",
        label: "Invoices",
        render: (item) => {
          // Prioritize the billPDFUrls array from the API
          let pdfs = item?.billPDFUrls || [];

          // Fallback parsing for other formats if billPDFUrls is missing
          if (pdfs.length === 0) {
            const rawValue = item?.billPDFUrl || item?.billPDF;
            if (rawValue) {
              if (Array.isArray(rawValue)) {
                pdfs = rawValue;
              } else {
                try {
                  pdfs = JSON.parse(rawValue);
                  if (!Array.isArray(pdfs)) pdfs = [rawValue];
                } catch (e) {
                  pdfs = [rawValue];
                }
              }
            }
          }

          if (pdfs.length === 0)
            return <span className="text-slate-400">-</span>;

          const apiUrl =
            process.env.REACT_APP_API_URL || "http://localhost:8040/api/v1";

          return (
            <div className="flex flex-wrap gap-1.5">
              {pdfs.map((path, idx) => {
                if (typeof path !== "string") return null;
                const fileUrl = path.startsWith("http")
                  ? path
                  : `${apiUrl}/../${path}`;
                return (
                  <a
                    key={idx}
                    href={fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-rose-50 text-rose-600 hover:bg-rose-100 hover:text-rose-700 transition-all border border-rose-100 shadow-sm group"
                    title={`View Invoice ${idx + 1}`}
                  >
                    <svg
                      className="h-3 w-3 transition-transform group-hover:scale-110"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M11.363 2c4.155 0 2.637 6 2.637 6s6-1.518 6 2.638v11.362c0 .552-.448 1-1 1h-13c-.552 0-1-.448-1-1v-19c0-.552.448-1 1-1h6.363zm.637-2h-8c-1.104 0-2 .896-2 2v20c0 1.104.896 2 2 2h16c1.104 0 2-.896 2-2v-13l-10-9z" />
                    </svg>
                    <span className="text-[9px] font-black uppercase tracking-tighter">
                      INV {idx + 1}
                    </span>
                  </a>
                );
              })}
            </div>
          );
        },
      },
      {
        key: "isActive",
        label: "Status",
        headerClassName: "text-center",
        className: "text-center",
        render: (item) => (
          <div className="flex flex-col items-center gap-1">
            <span
              className={`inline-flex rounded-full px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wider ${
                item?.isActive
                  ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400"
                  : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400"
              }`}
            >
              {item?.isActive ? "Active" : "Inactive"}
            </span>
            {item?.isConverted && (
              <span className="inline-flex rounded-full bg-blue-100 px-2.5 py-0.5 text-[9px] font-black uppercase tracking-wider text-blue-700 dark:bg-blue-500/20 dark:text-blue-400">
                Converted
              </span>
            )}
          </div>
        ),
      },
      {
        key: "isPaid",
        label: "Is Paid",
        headerClassName: "text-center",
        className: "text-center",
        sortable: true,
        render: (item) => (
          <span
            className={`inline-flex rounded-full px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wider ${
              item?.isPaid
                ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400"
                : "bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-400"
            }`}
          >
            {item?.isPaid ? "Paid" : "Unpaid"}
          </span>
        ),
      },
    ],
    [],
  );

  const findBlIdByPurchaseMasterId = async (item) => {
    const purchaseMasterId = item?.purchaseMasterId;
    if (!purchaseMasterId) return null;

    try {
      // Use billNo or bookingNo to narrow down the search
      const searchTerm = item.billNo || item.bookingNo || "";
      const response = await apiClient.get(`/bl-master`, {
        params: { search: searchTerm, limit: 100 },
      });

      const payload = response?.data?.data || response?.data || [];
      const items = Array.isArray(payload) ? payload : payload.data || [];

      // Try to match by pmId/purchaseMasterId first
      let bl = items.find(
        (b) =>
          String(b.pmId || b.purchaseMasterId) === String(purchaseMasterId),
      );

      // Fallback: match by billNo and bookingNo if IDs are missing in response
      if (!bl && (item.billNo || item.bookingNo)) {
        bl = items.find(
          (b) =>
            (item.billNo && String(b.billNo) === String(item.billNo)) ||
            (item.bookingNo && String(b.bookingNo) === String(item.bookingNo)),
        );
      }

      return bl?.blId || null;
    } catch (err) {
      console.error("BL lookup failed", err);
      return null;
    }
  };

  const openCreate = () => navigate(`${basePath}/new`);
  const openEdit = (item) => {
    const recordId = item?.purchaseMasterId;
    if (!recordId) {
      alertService.error("Record ID is required for edit.");
      return;
    }
    // Logic moved to form page to allow partial editing of primary details
    navigate(`${basePath}/${encrypt(recordId)}/edit`);
  };

  const openView = (item) => {
    const recordId = item?.purchaseMasterId;
    if (!recordId) {
      alertService.error("Record ID is required for view.");
      return;
    }
    navigate(`${basePath}/${encrypt(recordId)}/view`);
  };

  const openLinkedBlMaster = async (item) => {
    const purchaseMasterId = item?.purchaseMasterId;
    if (!purchaseMasterId)
      return alertService.error("Purchase Master ID is required.");

    if (item?.isConverted) {
      alertService.info("All the stock items of this purchase are converted.");
      return;
    }

    const blId = await findBlIdByPurchaseMasterId(item);
    if (blId) {
      navigate(`/bl-master/${encrypt(blId)}/edit`);
      return;
    }

    // If no BL found, redirect to create new BL with PM context
    navigate(`/bl-master/new?pmId=${encrypt(purchaseMasterId)}`);
  };

  return (
    <div className="space-y-3">
      {error ? <ErrorAlert message={error} /> : null}

      <CrudTable
        columns={tableColumns}
        rows={rows}
        isLoading={isLoading}
        pagination={pagination}
        sortConfig={sortConfig}
        filters={filters}
        onFiltersChange={updateFilters}
        onSort={changeSort}
        onPageChange={changePage}
        renderActions={(item) => (
          <div className="flex items-center gap-1.5">
            {item?.isPaid ? (
              <Button
                variant="secondary"
                size="sm"
                className="px-2.5"
                onClick={() => openView(item)}
              >
                <svg className="mr-1 h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                View
              </Button>
            ) : (
              <>
                <Button
                  variant="secondary"
                  size="sm"
                  className="h-8 w-12 !p-0 font-black text-[10px]"
                  onClick={() => openLinkedBlMaster(item)}
                  title="View linked BL Master"
                >
                  BL
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  className="px-2.5"
                  onClick={() => openEdit(item)}
                >
                  <svg className="mr-1 h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Edit
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  className="px-2.5"
                  onClick={() => {
                    if (item?.isConverted) {
                      alertService.warning("Converted records cannot be deleted.");
                      return;
                    }
                    askDelete(item);
                  }}
                >
                  <svg className="mr-1 h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Delete
                </Button>
              </>
            )}
          </div>
        )}
        searchKey="search"
        searchPlaceholder="Search by Bill No, Booking No, or Vendor..."
        tableScrollHeight="1500px"
        toolbarAction={
          <Button
            onClick={openCreate}
            className="shadow-lg shadow-primary-500/20"
          >
            + New Purchase
          </Button>
        }
      />
    </div>
  );
}

export default PurchaseMasterListPage;
